import { ReactNode, useCallback, useRef, useState } from 'react';

import { RiImageLine } from '@remixicon/react';

import { Lightbox, PreviewableImage } from '@spotify/confidence-core-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';
import {
  DEFAULT_ARTIFACT_ICON,
  ExternalUrlArtifactListItem,
  createArtifactExtension,
  isExternalLink,
  isImageArtifactLabel,
} from '@spotify/plugin-confidence-projects-react';

import { projectsPlugin } from '../plugin';

const ImageArtifactListItem = ({
  artifact,
  icon,
  onDelete,
  onRename,
}: {
  artifact: AttachmentFragment;
  icon: ReactNode;
  onDelete?: () => void;
  onRename?: () => void;
}) => {
  const [originRect, setOriginRect] = useState<DOMRect | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const handleOpen = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect) setOriginRect(rect);
  }, []);

  return (
    <div ref={rootRef}>
      <ExternalUrlArtifactListItem
        artifact={artifact}
        icon={icon}
        subtitleLabel="Image"
        openLabel="View image"
        onOpen={handleOpen}
        onDelete={onDelete}
        onRename={onRename}
      />
      {originRect && (
        <Lightbox
          src={artifact.uri}
          alt={artifact.displayName ?? 'Image'}
          originRect={originRect}
          onClose={() => setOriginRect(null)}
        />
      )}
    </div>
  );
};

export const ImageArtifactExtension = projectsPlugin.provide(
  createArtifactExtension({
    id: 'image',
    predicate: artifact =>
      isExternalLink(artifact.uri) && isImageArtifactLabel(artifact.labels),
    icon: () => ({ ...DEFAULT_ARTIFACT_ICON, Icon: RiImageLine }),
    label: 'Image',
    category: 'Images',
    render: artifact => (
      <PreviewableImage
        src={artifact.uri}
        alt={artifact.displayName ?? 'Image'}
        width="100%"
        borderRadius="base"
      />
    ),
    renderListItem: (artifact, { onDelete, onRename, icon }) => (
      <ImageArtifactListItem
        artifact={artifact}
        icon={icon}
        onDelete={onDelete}
        onRename={onRename}
      />
    ),
  }),
);
